import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, Users, ClipboardCheck, Stethoscope, KeyRound } from "lucide-react";

const COORD_AREAS = ["Inglés", "Francés", "Académica", "Áreas especiales", "Música", "Deportes", "Administrativa"];

export const StaffManager = () => {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [kind, setKind] = useState("administracion");
  const [title, setTitle] = useState("coordinador");
  const [area, setArea] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/staff"); setItems(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const admins = useMemo(() => items.filter((s) => !["coordinacion", "enfermeria"].includes(s.staff_kind || "administracion")), [items]);
  const coords = useMemo(() => items.filter((s) => s.staff_kind === "coordinacion"), [items]);
  const nurses = useMemo(() => items.filter((s) => s.staff_kind === "enfermeria"), [items]);
  const limitReached = kind === "coordinacion" ? coords.length >= 4 : kind === "enfermeria" ? nurses.length >= 4 : admins.length >= 3;

  const pickKind = (k) => {
    setKind(k);
    if (k === "coordinacion") setTitle("coordinador");
    else if (k === "enfermeria") setTitle("enfermero");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) { toast.error("Correo válido y contraseña de 6+ caracteres"); return; }
    if (kind === "enfermeria" && !name.trim()) { toast.error("Escribe el nombre completo de la enfermera/enfermero"); return; }
    setSaving(true);
    try {
      const body = { email, password, name, kind };
      if (kind === "coordinacion") { body.title = title; body.area = area; }
      if (kind === "enfermeria") { body.title = title; }
      await api.post("/staff", body);
      toast.success({ coordinacion: "Cuenta de coordinación habilitada", enfermeria: "Cuenta de enfermería habilitada" }[kind] || "Cuenta administrativa habilitada");
      setEmail(""); setName(""); setPassword(""); setArea(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const remove = async (uid) => {
    if (!window.confirm("¿Eliminar esta cuenta?")) return;
    try { await api.delete(`/staff/${uid}`); toast.success("Cuenta eliminada"); load(); } catch { toast.error("Error"); }
  };
  const updateArea = async (uid, newArea) => {
    try { await api.patch(`/staff/${uid}`, { area: newArea }); setItems((its) => its.map((s) => s.user_id === uid ? { ...s, area: newArea } : s)); toast.success("Área actualizada"); }
    catch { toast.error("No se pudo actualizar el área"); }
  };
  const changePassword = async (uid, pw) => {
    if ((pw || "").trim().length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return false; }
    try { await api.patch(`/staff/${uid}`, { password: pw.trim() }); toast.success("Contraseña actualizada"); return true; }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo cambiar la contraseña"); return false; }
  };

  const Row = ({ s }) => {
    const isCoord = s.staff_kind === "coordinacion";
    const isNurse = s.staff_kind === "enfermeria";
    const [pwOpen, setPwOpen] = useState(false);
    const [pw, setPw] = useState("");
    const [pwBusy, setPwBusy] = useState(false);
    const savePw = async () => { setPwBusy(true); const ok = await changePassword(s.user_id, pw); setPwBusy(false); if (ok) { setPw(""); setPwOpen(false); } };
    const badge = isCoord ? (s.title === "coordinadora" ? "Coordinadora" : "Coordinador")
      : isNurse ? (s.title === "enfermera" ? "Enfermera" : "Enfermero") : "Administración";
    const badgeCls = isCoord ? "bg-indigo-100 text-indigo-700" : isNurse ? "bg-rose-100 text-rose-700" : "bg-nardo-100 text-nardo-600";
    return (
      <div className="border border-nardo-100 rounded-md p-3" data-testid="staff-row">
        <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-navy font-medium truncate flex items-center gap-2">
            {s.name || (isCoord ? "Coordinación" : isNurse ? "Enfermería" : "Administrativo")}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${badgeCls}`}>{badge}</span>
          </div>
          <div className="text-[11px] text-nardo-500 truncate">{s.email}</div>
          {isCoord && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-nardo-400">{s.title === "coordinadora" ? "Coordinadora de" : "Coordinador de"}</span>
              <select value={s.area || ""} onChange={(e) => updateArea(s.user_id, e.target.value)} data-testid={`staff-area-${s.user_id}`}
                className="text-[11px] border border-nardo-200 rounded-md px-1.5 py-0.5 bg-white text-indigo-700 font-medium outline-none focus:border-indigo-400">
                <option value="">— Sin asignar —</option>
                {COORD_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setPwOpen((v) => !v)} title="Cambiar contraseña" className="p-2 text-nardo-500 hover:text-navy" data-testid={`staff-pw-toggle-${s.user_id}`}><KeyRound className="w-4 h-4" /></button>
          <button onClick={() => remove(s.user_id)} className="p-2 text-nardo-500 hover:text-destructive" data-testid={`staff-delete-${s.user_id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
        </div>
        {pwOpen && (
          <div className="mt-2 pt-2 border-t border-nardo-100 flex items-center gap-2" data-testid={`staff-pw-form-${s.user_id}`}>
            <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nueva contraseña (6+)" data-testid={`staff-pw-input-${s.user_id}`}
              className="flex-1 border border-nardo-200 rounded-md px-2.5 py-1.5 text-sm text-navy outline-none focus:border-navy" />
            <button onClick={savePw} disabled={pwBusy} data-testid={`staff-pw-save-${s.user_id}`} className="text-xs font-semibold bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-md disabled:opacity-50">{pwBusy ? "…" : "Guardar"}</button>
            <button onClick={() => { setPw(""); setPwOpen(false); }} className="text-xs text-nardo-500 hover:text-navy px-2 py-1.5">Cancelar</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="staff-manager">
      <h2 className="font-display font-semibold text-xl text-navy mb-1 flex items-center gap-2"><UserCog className="w-5 h-5 text-navy-light" /> Cuentas: administración, coordinación y enfermería</h2>
      <p className="text-sm text-nardo-500 mb-5">Habilita hasta <strong>3 administrativos</strong>, <strong>4 de coordinación</strong> y <strong>4 de enfermería</strong>. Enfermería captura peso, talla, temperatura, alergias y la ficha médica de cada alumno. <span className="text-navy font-medium">{admins.length}/3 admin · {coords.length}/4 coord · {nurses.length}/4 enfermería.</span></p>

      <form onSubmit={create} className="bg-nardo-50/60 border border-nardo-100 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap gap-2 mb-3">
          <button type="button" onClick={() => pickKind("administracion")} data-testid="staff-kind-admin" className={`text-xs px-3 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors ${kind === "administracion" ? "bg-navy text-white" : "bg-white border border-nardo-200 text-nardo-600"}`}><UserCog className="w-4 h-4" /> Administración</button>
          <button type="button" onClick={() => pickKind("coordinacion")} data-testid="staff-kind-coord" className={`text-xs px-3 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors ${kind === "coordinacion" ? "bg-indigo-600 text-white" : "bg-white border border-nardo-200 text-nardo-600"}`}><ClipboardCheck className="w-4 h-4" /> Coordinación</button>
          <button type="button" onClick={() => pickKind("enfermeria")} data-testid="staff-kind-nurse" className={`text-xs px-3 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors ${kind === "enfermeria" ? "bg-rose-600 text-white" : "bg-white border border-nardo-200 text-nardo-600"}`}><Stethoscope className="w-4 h-4" /> Enfermería</button>
        </div>
        {kind === "enfermeria" && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="staff-nurse-title-group">
            <span className="text-xs text-nardo-500 self-center">Título:</span>
            {["enfermero", "enfermera"].map((t) => (
              <button key={t} type="button" onClick={() => setTitle(t)} data-testid={`staff-title-${t}`} className={`text-xs px-3 py-1.5 rounded-full font-semibold capitalize transition-colors ${title === t ? "bg-rose-100 text-rose-700 ring-1 ring-rose-300" : "bg-white border border-nardo-200 text-nardo-600"}`}>{t}</button>
            ))}
          </div>
        )}
        {kind === "coordinacion" && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="staff-title-group">
            <span className="text-xs text-nardo-500 self-center">Título:</span>
            {["coordinador", "coordinadora"].map((t) => (
              <button key={t} type="button" onClick={() => setTitle(t)} data-testid={`staff-title-${t}`} className={`text-xs px-3 py-1.5 rounded-full font-semibold capitalize transition-colors ${title === t ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" : "bg-white border border-nardo-200 text-nardo-600"}`}>{t}</button>
            ))}
            <span className="text-xs text-nardo-500 self-center ml-2">{title === "coordinadora" ? "Coordinadora de:" : "Coordinador de:"}</span>
            <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="staff-area-select"
              className="text-xs border border-nardo-200 rounded-md px-2 py-1.5 bg-white text-indigo-700 font-medium outline-none focus:border-indigo-400">
              <option value="">— Selecciona área —</option>
              {COORD_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input data-testid="staff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
          <input data-testid="staff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
          <input data-testid="staff-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña (6+)" className="border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
          <button data-testid="staff-add" type="submit" disabled={saving || limitReached} title={limitReached ? "Límite alcanzado" : ""} className={`inline-flex items-center justify-center gap-2 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 ${kind === "coordinacion" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-navy hover:bg-navy-light"}`}><Plus className="w-4 h-4" /> Habilitar</button>
        </div>
        {limitReached && <p className="text-[11px] text-red-500 mt-2">Alcanzaste el límite de cuentas de {kind === "coordinacion" ? "coordinación (4)" : kind === "enfermeria" ? "enfermería (2)" : "administración (3)"}.</p>}
      </form>

      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5"><UserCog className="w-4 h-4 text-navy-light" /> Administración ({admins.length}/3)</h3>
          {admins.length === 0 ? <p className="text-sm text-nardo-400">Sin cuentas administrativas.</p> : <div className="space-y-2">{admins.map((s) => <Row key={s.user_id} s={s} />)}</div>}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5"><Users className="w-4 h-4 text-indigo-600" /> Coordinación ({coords.length}/4)</h3>
          {coords.length === 0 ? <p className="text-sm text-nardo-400">Sin cuentas de coordinación.</p> : <div className="space-y-2">{coords.map((s) => <Row key={s.user_id} s={s} />)}</div>}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5"><Stethoscope className="w-4 h-4 text-rose-600" /> Enfermería ({nurses.length}/2)</h3>
          {nurses.length === 0 ? <p className="text-sm text-nardo-400">Sin cuentas de enfermería.</p> : <div className="space-y-2">{nurses.map((s) => <Row key={s.user_id} s={s} />)}</div>}
        </div>
      </div>
    </div>
  );
};
